import React, { useState } from 'react';
import { ActionActionTypeEnum, TWidgetProps } from '@libs/types';
import { useBehavior, useTriggerAction, useUpdateWidgetValue } from '@libs/core';
import { CoreListItemText } from './CoreListItemText';
import { CoreListLeadingEl } from './CoreListLeadingEl';
import { CoreListTrailingEl } from './CoreListTrailingEl';
import { useThemeHook } from '../hooks/themeHook';
import { TCoreOptions } from '../types/options';
import { TCoreListData, CoreListItem } from '../types/data';
import { getCommonStyles, getPaddingStyles } from '../utils/styles';
import styles from './list.module.css';

const CoreList = ({ widget }: TWidgetProps<TCoreListData, TCoreOptions>) => {
    const { palette } = useThemeHook(widget);
    const { isHidden } = useBehavior(widget);
    const { updateWidgetValue } = useUpdateWidgetValue(widget);
    const { triggerActionsByType } = useTriggerAction(widget);
    const [selectedIndex, setSelectedIndex] = useState<number | undefined>(undefined);
    const { data: { items = [] } = {} } = widget;

    const handleSelectItem = (item: CoreListItem, index: number) => {
        const value = item?.value;
        setSelectedIndex(index);
        updateWidgetValue(value);
        triggerActionsByType(ActionActionTypeEnum.onItemSelected, { value }).then();
    };

    if (items.length === 0) {
        return (
            <div
                data-test={widget?.uid}
                style={{
                    ...getCommonStyles(palette, isHidden),
                    ...getPaddingStyles(palette),
                }}
            />
        );
    }

    return (
        <div
            data-test={widget?.uid}
            style={{
                ...getCommonStyles(palette, isHidden),
                ...getPaddingStyles(palette),
            }}
        >
            <ul className={styles.list}>
                {items.map((item, index) => {
                    const isSelected = index === selectedIndex;
                    const isDisabled = item?.disabled ?? false;
                    const itemClassName = [styles.item, item?.showDivider && styles.itemDivided].filter(Boolean).join(' ');
                    const buttonClassName = [styles.itemButton, isSelected && styles.itemButtonSelected, isDisabled && styles.itemButtonDisabled]
                        .filter(Boolean)
                        .join(' ');

                    return (
                        <li
                            key={item.value}
                            data-test={`item-${widget?.uid}`}
                            className={itemClassName}
                            style={{
                                backgroundColor: palette?.onSurface,
                                borderBottomColor: item?.showDivider ? palette.onSurfaceVariant : undefined,
                            }}
                        >
                            <button
                                type="button"
                                data-test={`button-${widget?.uid}`}
                                className={buttonClassName}
                                disabled={isDisabled}
                                onClick={() => handleSelectItem(item, index)}
                            >
                                <CoreListLeadingEl item={item} palette={palette} dataTestId={`avatar-${widget?.uid}`} />
                                <CoreListItemText palette={palette} primary={item.title} secondary={item.subtitle} />
                                <span data-test={`trailing-${widget?.uid}`} className={styles.trailing}>
                                    <CoreListTrailingEl item={item} palette={palette} />
                                </span>
                            </button>
                        </li>
                    );
                })}
            </ul>
        </div>
    );
};

export { CoreList };
